import { SupabaseClient } from '@supabase/supabase-js';
import {
  RespondentCode,
  RespondentCodeType,
  RespondentCodeWType
} from '@/types/pulsedb';
import { QuantGraphRow } from '@/types/pulse';
import { validateRespondentCodeValue } from '@/utils/pulse/respondentCodes';
import { displayableCodes } from '@/utils/pulse/codeValues';

/**
 * The columns a workspace sets on a respondent codetype. `datatype` and
 * `multiple` are NOT NULL on the table, so both are required on insert.
 */
export interface RespondentCodeTypeInput {
  name: string;
  description?: string | null;
  key?: string | null;
  datatype: number;
  multiple: boolean;
}

/** Every live codetype defined by a workspace, ordered the way the table shows them. */
export const getRespondentCodetypes = async (
  supabase: SupabaseClient,
  workspaceId: string
): Promise<RespondentCodeType[]> => {
  const { data } = await supabase
    .from('respondent_codetypes')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('deleted', false)
    .order('name', { ascending: true })
    .throwOnError();

  return (data ?? []) as unknown as RespondentCodeType[];
};

/**
 * Which of the given codetypes already hold at least one value. Returned as a
 * plain array — this rides inside an SWR payload, and SWR's change detection
 * treats every Set as equal to every other Set.
 *
 * A codetype's datatype and `multiple` describe the values recorded under it, so
 * the table locks both once anything has been recorded — changing them would
 * leave existing codes describing something the attribute no longer claims to be.
 * Values held only by deleted respondents don't count: the panel and the value
 * counts both show nothing recorded, so the definition must read as editable on
 * the same terms.
 *
 * Each id is probed on its own rather than pulled back in one `in` filter: only
 * the existence of a value matters, so a `limit(1)` per attribute avoids reading
 * every code in the workspace.
 */
export const getCodetypesWithValues = async (
  supabase: SupabaseClient,
  codetypeIds: number[]
): Promise<number[]> => {
  const probes = await Promise.all(
    codetypeIds.map(async (id) => {
      const { data } = await supabase
        .from('respondent_codes')
        .select('id, respondents!inner(id)')
        .eq('codetype_id', id)
        .eq('respondents.deleted', false)
        .limit(1)
        .throwOnError();

      return (data ?? []).length > 0 ? id : null;
    })
  );

  return probes.filter((id): id is number => id !== null);
};

export const insertRespondentCodeType = async (
  supabase: SupabaseClient,
  workspaceId: string,
  input: RespondentCodeTypeInput
): Promise<RespondentCodeType> => {
  const { data } = await supabase
    .from('respondent_codetypes')
    .insert({ ...input, workspace_id: workspaceId })
    .select()
    .single()
    .throwOnError();

  return data as unknown as RespondentCodeType;
};

export const updateRespondentCodeType = async (
  supabase: SupabaseClient,
  id: number,
  input: Partial<RespondentCodeTypeInput>
): Promise<RespondentCodeType> => {
  const { data } = await supabase
    .from('respondent_codetypes')
    .update(input)
    .eq('id', id)
    .select()
    .single()
    .throwOnError();

  return data as unknown as RespondentCodeType;
};

/**
 * Brings a deleted codetype back under a new definition, keeping its id.
 *
 * The unique index on (key, workspace_id) covers deleted rows, so a key stays
 * held after a delete and defining it again reuses that row. What the codetype
 * meant before does not survive that: the definition is replaced wholesale, so
 * the values recorded under the old one are cleared rather than reattached to a
 * field that may now mean something else entirely.
 */
export const reviveRespondentCodeType = async (
  supabase: SupabaseClient,
  id: number,
  input: RespondentCodeTypeInput
): Promise<RespondentCodeType> => {
  await supabase
    .from('respondent_codes')
    .delete()
    .eq('codetype_id', id)
    .throwOnError();

  const { data } = await supabase
    .from('respondent_codetypes')
    .update({ ...input, deleted: false })
    .eq('id', id)
    .select()
    .single()
    .throwOnError();

  return data as unknown as RespondentCodeType;
};

/**
 * Soft-deletes a codetype. The row stays for the `respondent_codes` that point at
 * it, so nothing a workspace has already recorded is destroyed by removing the
 * attribute it was recorded under.
 */
export const deleteRespondentCodeType = async (
  supabase: SupabaseClient,
  id: number
): Promise<void> => {
  await supabase
    .from('respondent_codetypes')
    .update({ deleted: true })
    .eq('id', id)
    .throwOnError();
};

/**
 * Checks a value against the attribute it is being stored under and returns the
 * string to persist, throwing when the two disagree. Both writers below go
 * through it, so no caller can record a value its attribute does not describe.
 */
const validatedCodeValue = async (
  supabase: SupabaseClient,
  codetypeId: number,
  value: string
): Promise<string> => {
  const { data: codetype } = await supabase
    .from('respondent_codetypes')
    .select('datatype')
    .eq('id', codetypeId)
    .single()
    .throwOnError();

  const check = validateRespondentCodeValue(codetype.datatype, value);
  if (!check.ok) throw new Error(check.error);
  return check.value;
};

/** Records one value against a respondent for a codetype. */
export const insertRespondentCode = async (
  supabase: SupabaseClient,
  respondentId: number,
  codetypeId: number,
  value: string
): Promise<RespondentCode> => {
  const { data } = await supabase
    .from('respondent_codes')
    .insert({
      respondent_id: respondentId,
      codetype_id: codetypeId,
      value: await validatedCodeValue(supabase, codetypeId, value)
    })
    .select()
    .single()
    .throwOnError();

  return data as unknown as RespondentCode;
};

/**
 * One respondent's codes with the attribute each belongs to, for the panel that
 * lists them. Codes under a deleted attribute are left out — the attribute is
 * gone from the workspace, so its values have nothing to be shown against.
 */
export const getRespondentCodesWithTypes = async (
  supabase: SupabaseClient,
  respondentId: number
): Promise<RespondentCodeWType[]> => {
  const { data } = await supabase
    .from('respondent_codes')
    .select('*, respondent_codetypes!inner(*)')
    .eq('respondent_id', respondentId)
    .eq('respondent_codetypes.deleted', false)
    .order('id', { ascending: true })
    .throwOnError();

  return (data ?? []) as unknown as RespondentCodeWType[];
};

export const updateRespondentCode = async (
  supabase: SupabaseClient,
  id: number,
  value: string
): Promise<void> => {
  // The attribute comes from the row being edited: an update carries only the
  // new value, not what it is meant to be.
  const { data: existing } = await supabase
    .from('respondent_codes')
    .select('codetype_id')
    .eq('id', id)
    .single()
    .throwOnError();

  await supabase
    .from('respondent_codes')
    .update({
      value: await validatedCodeValue(supabase, existing.codetype_id, value)
    })
    .eq('id', id)
    .throwOnError();
};

/** Removes one recorded value. `respondent_codes` keeps no deleted flag. */
export const deleteRespondentCode = async (
  supabase: SupabaseClient,
  id: number
): Promise<void> => {
  await supabase.from('respondent_codes').delete().eq('id', id).throwOnError();
};

/** Every code held by the given respondents, keyed by respondent id. */
export const getRespondentCodes = async (
  supabase: SupabaseClient,
  respondentIds: number[]
): Promise<Map<number, RespondentCode[]>> => {
  const byRespondent = new Map<number, RespondentCode[]>();
  if (respondentIds.length === 0) return byRespondent;

  const { data } = await supabase
    .from('respondent_codes')
    .select('*')
    .in('respondent_id', respondentIds)
    .order('id', { ascending: true })
    .throwOnError();

  ((data ?? []) as unknown as RespondentCode[]).forEach((code) => {
    const codes = byRespondent.get(code.respondent_id) ?? [];
    codes.push(code);
    byRespondent.set(code.respondent_id, codes);
  });

  return byRespondent;
};

/**
 * How many respondents hold each value of a codetype, most common first, for the
 * value-counts dialog.
 *
 * Deleted respondents are dropped in a second pass rather than an `!inner` join:
 * `respondent_codes.respondent_id` carries a foreign key to `respondent_codetypes`,
 * so PostgREST has no relationship to embed `respondents` through.
 */
export const getRespondentCodeValueCounts = async (
  supabase: SupabaseClient,
  codetypeId: number
): Promise<{ rows: QuantGraphRow[]; total: number }> => {
  const { data } = await supabase
    .from('respondent_codes')
    .select('value, respondent_id')
    .eq('codetype_id', codetypeId)
    .throwOnError();

  const { data: codetype } = await supabase
    .from('respondent_codetypes')
    .select('id, datatype')
    .eq('id', codetypeId)
    .single()
    .throwOnError();

  // Counted on the same terms the panel displays, so the graph and the badges
  // never disagree about how many people hold a value.
  const codes = displayableCodes(
    ((data ?? []) as { value: string | null; respondent_id: number }[]).map(
      (code) => ({ ...code, codetype_id: codetypeId })
    ),
    [codetype],
    'attribute value counts'
  );
  if (codes.length === 0) return { rows: [], total: 0 };

  const { data: live } = await supabase
    .from('respondents')
    .select('id')
    .in('id', Array.from(new Set(codes.map((code) => code.respondent_id))))
    .eq('deleted', false)
    .throwOnError();

  const liveIds = new Set((live ?? []).map((row) => row.id as number));
  const counts = new Map<string, number>();
  let total = 0;

  codes.forEach((code) => {
    if (!liveIds.has(code.respondent_id)) return;
    const label = code.value?.trim() || '(empty)';
    counts.set(label, (counts.get(label) ?? 0) + 1);
    total += 1;
  });

  const rows: QuantGraphRow[] = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([label, count]) => ({ label, count }));

  return { rows, total };
};
